import {
  Affix,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Progress,
  Row,
  Tooltip,
  Typography,
} from "@payping/web-components";
import { CheckCircleFilled, CloseCircleFilled, CuToman, FiAlertCircle } from "@payping/icons";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import React from "react";
import { FormInstance } from "@payping/web-components/components/Form/types";
import {
  CreateSamanStepEnum,
  SamanConfiguration,
  SamanCreateFormType,
  SamanOverviewType,
} from "@/pages/DigitalAccount/Saman/Create";
import { useParams } from "react-router-dom";
import AnchorSamanButtons from "@/pages/DigitalAccount/Saman/Create/Anchor/buttons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { AgreementStateEnum, TransferMethodEnum } from "@payping/api";
import { SamanRowType } from "@/pages/DigitalAccount/Saman/Create/SecondStep";
import { useAsync } from "react-use";
import { getDepositBalance } from "@payping/redux/actions";
import { BankTypeEnum } from "@payping/api/app/api/digitalAccountBankAgreementsTypes";

interface Props {
  formStepOne: FormInstance<SamanConfiguration>;
  formStepTwo: FormInstance<SamanRowType>;
  formStepThree: FormInstance<SamanCreateFormType>;
  formTransfer: FormInstance;
  setOpenFinalDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  overviewData: SamanOverviewType;
  loadingStepTwo: boolean;
  loadingDelete: boolean;
  requestTitle: string;
  requestDesc: string;
  transferType?: TransferMethodEnum;
  setTransferType: React.Dispatch<React.SetStateAction<TransferMethodEnum | undefined>>;
  prev: () => void;
  next: () => void;
}

// interface TransferData {
//   content: ReactNode;
// }

const DataCardStepSaman: React.FC<Props> = ({
  formStepOne,
  formStepTwo,
  loadingDelete,
  prev,
  formStepThree,
  setOpenFinalDrawer,
  next,
  loadingStepTwo,
  overviewData,
  requestTitle,
  requestDesc,
}) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const { step } = useParams<{ step: string }>() as { step: string };
  const stepNumber = parseInt(step, 10) as CreateSamanStepEnum;
  const { balance } = useAppSelector((state) => state.digitalAccount.saman.depositBalance);
  const bankAgreement = useAppSelector((state) => state.digitalAccount.bankAgreement);
  const paymentDetailsModels = useAppSelector((state) => state.digitalAccount.saman.requestList?.paymentDetailsModels);
  const { lg } = Grid.useBreakpoint();
  // const { getLabelTransfer } = useTransferLabel();

  useAsync(async () => {
    const confirmedSamanAgreement = bankAgreement.find(
      (agreement) =>
        agreement.bankType === BankTypeEnum.Saman && agreement.agreementInfo?.state === AgreementStateEnum.Confirmed,
    );
    if (confirmedSamanAgreement) {
      await dispatch(getDepositBalance());
    }
  }, [dispatch]);

  const errorCount = paymentDetailsModels?.reduce((acc: any, item: any) => acc + (item.errors ? 1 : 0), 0);

  const getAmount = paymentDetailsModels?.reduce((acc: any, item) => acc + (item.amount ?? 1, 10) / 10, 0) || 0;

  const withdrawableBalance = Math.trunc((balance || 0) / 10);

  // const Transfer: TransferData = {
  //   content: (
  //     <Form
  //       onValuesChange={(changedValues) => {
  //         if ("transferType" in changedValues) {
  //           setTransferType(changedValues.transferType);
  //         }
  //       }}
  //       form={formTransfer}
  //     >
  //       <Form.Item noStyle name={"transferType"}>
  //         <Radio.Group className="tw-w-full" data-google-track={typeId}>
  //           {!(getAmount >= minAmountTransfer && getAmount <= maxAmountTransfer) ? (
  //             <Radio.Button value={TransferMethodEnum.AccountToAccount}>
  //               {t("digitalAccount.samanCreate.transferType.accountToAccount")}
  //             </Radio.Button>
  //           ) : null}
  //           <Radio.Button value={TransferMethodEnum.Paya}>
  //             {t("digitalAccount.samanCreate.transferType.paya")}
  //           </Radio.Button>
  //           <Radio.Button value={TransferMethodEnum.Satna}>
  //             {t("digitalAccount.samanCreate.transferType.satna")}
  //           </Radio.Button>
  //         </Radio.Group>
  //       </Form.Item>
  //     </Form>
  //   ),
  // };

  return (
    <Affix offsetTop={20}>
      <Card.Ant
        title={
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between tw-pt-4"}>
              <Flex vertical>
                <Typography.Text type={"secondary"}>{t("digitalAccount.samanCreate.transferAmount")}</Typography.Text>

                <Flex align="center">
                  <Typography.Title
                    level={5}
                  >{`${formatLocalizedNumber({ value: Number(getAmount) })} ${t("common.from")} ${formatLocalizedNumber({ value: withdrawableBalance })}`}</Typography.Title>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>
              <Progress
                strokeLinecap="square"
                type="circle"
                percent={withdrawableBalance && getAmount ? Math.trunc((getAmount / withdrawableBalance) * 100) : 0}
                size={20}
              />
            </div>
          </Form.Item>
        }
      >
        <Flex vertical className="tw-w-full" gap={"middle"}>
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <div className={"tw-me-2 tw-shrink-0"}>
                <Typography.Text type={"secondary"}>{t("digitalAccount.samanCreate.transferName")}</Typography.Text>
              </div>

              <Typography.Text ellipsis>
                <Tooltip title={requestTitle}>{requestTitle}</Tooltip>
              </Typography.Text>
            </div>
          </Form.Item>
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <div className={"tw-me-2 tw-shrink-0"}>
                <Typography.Text type={"secondary"}>{t("description")}</Typography.Text>
              </div>

              <Typography.Text ellipsis>
                <Tooltip title={requestDesc}>{requestDesc}</Tooltip>
              </Typography.Text>
            </div>
          </Form.Item>
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <Typography.Text type={"secondary"}>{t("digitalAccount.samanCreate.transferType.title")}</Typography.Text>
              <Flex gap={"small"}>
                <Typography.Text>{t("digitalAccount.samanCreate.transferType.auto")}</Typography.Text>
                <FiAlertCircle width={14} className="color--black" />
              </Flex>
            </div>
          </Form.Item>
          {stepNumber === CreateSamanStepEnum.CreateTransfer ? (
            <>
              <Divider className={"tw-m-0"} />
              <Form.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>
                      {t("digitalAccount.samanCreate.requestNumber")}
                    </Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw-flex tw-justify-end"}>
                    <Typography.Text>
                      {paymentDetailsModels?.length
                        ? t("settlement.create.showLength", { length: paymentDetailsModels.length })
                        : t("settlement.create.itemNotAdded")}
                    </Typography.Text>
                  </Col>
                </Row>
              </Form.Item>
              <Divider className={"!tw-m-0"} />
            </>
          ) : null}
          {stepNumber === CreateSamanStepEnum.Verification ? (
            <>
              <Divider className={"tw-m-0"} />
              <Form.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>
                      {t("digitalAccount.samanCreate.requestNumber")}
                    </Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw-flex tw-justify-end"}>
                    <Typography.Text>
                      {paymentDetailsModels?.length
                        ? t("settlement.create.showLength", { length: paymentDetailsModels.length })
                        : t("settlement.create.itemNotAdded")}
                    </Typography.Text>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("settlement.create.listStatus")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw-flex tw-justify-end"}>
                    {paymentDetailsModels?.length ? (
                      <Typography.Text>
                        {errorCount ? (
                          <Flex gap={"small"}>
                            <CloseCircleFilled width={14} className={"color--danger tw-item-center tw-flex"} />
                            {t("settlement.create.showError", { error: errorCount })}
                          </Flex>
                        ) : (
                          <Flex gap={"small"}>
                            <CheckCircleFilled width={14} className={"color--success tw-item-center tw-flex"} />
                            {t("settlement.create.noneError")}
                          </Flex>
                        )}
                      </Typography.Text>
                    ) : (
                      "-"
                    )}
                  </Col>
                </Row>
              </Form.Item>
            </>
          ) : null}
          {/* {(paymentDetailsModels?.length ?? 0) <= 1 && */}
          {/* getAmount >= minAmountTransfer && */}
          {/* getAmount <= maxAmountTransfer ? ( */}
          {/*  <> */}
          {/*    {stepNumber === CreateSamanStepEnum.Verification ? <Divider className={"tw-m-0"} /> : null} */}
          {/*    <Form.Item noStyle> */}
          {/*      <div className={"tw-flex tw-justify-between"}> */}
          {/*        <Typography.Text type={"secondary"}> */}
          {/*          {t("digitalAccount.samanCreate.transferType.title")} */}
          {/*        </Typography.Text> */}
          {/*        <Flex gap={"small"}> */}
          {/*          <Typography.Text>{getLabelTransfer(transferType || undefined)}</Typography.Text> */}
          {/*        </Flex> */}
          {/*      </div> */}
          {/*    </Form.Item> */}
          {/*    {Transfer.content} */}
          {/*  </> */}
          {/* ) : null} */}
          {stepNumber === CreateSamanStepEnum.Verification ? (
            <>
              <Divider className={"!tw-m-0"} />
              <Form.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("digitalAccount.samanCreate.totalCashOut")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw-flex tw-justify-end"}>
                    <Typography.Text>{overviewData?.total}</Typography.Text>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("digitalAccount.samanCreate.totalWage")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw-flex tw-justify-end"}>
                    <Typography.Text>{overviewData?.totalWage}</Typography.Text>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("digitalAccount.samanCreate.total")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw-flex tw-justify-end"}>
                    <Typography.Text>{overviewData?.totalAmount}</Typography.Text>
                  </Col>
                </Row>
              </Form.Item>
            </>
          ) : null}
          {lg ? (
            <Col xs={24}>
              <AnchorSamanButtons
                formStepOne={formStepOne}
                formStepTwo={formStepTwo}
                formStepThree={formStepThree}
                loadingDelete={loadingDelete}
                setOpenFinalDrawer={setOpenFinalDrawer}
                loadingStepTwo={loadingStepTwo}
                requestTitle={requestTitle}
                requestDescription={requestDesc}
                prev={prev}
                next={next}
              />
            </Col>
          ) : null}
        </Flex>
      </Card.Ant>
    </Affix>
  );
};

export default DataCardStepSaman;
